/**
 * DossierHero — cinematic hero image banner for the topic detail page.
 *
 * Renders the same AI-generated artwork used on the archive/homepage
 * cards, blown up to a full-bleed 16:9 banner above the CASE FILE
 * heading. Preserves the design lock (dark, moody, crimson accent) and
 * gives the click-through from archive → detail visual continuity.
 *
 * Renders nothing when the topic has no image_url — the detail page
 * gracefully falls back to the existing TopicHeader flow.
 */
import React from "react";
import { Clock, FileText, Link2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const _resolveImage = (src) => {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  return src.startsWith("/") ? `${API}${src}` : src;
};

const _STATUS_LABELS = {
  active: "ACTIVE", under_review: "UNDER REVIEW",
  archived: "ARCHIVED", closed: "CLOSED", restricted: "RESTRICTED",
  ongoing: "ACTIVE", published: "ACTIVE",
  monitored: "UNDER REVIEW", review: "UNDER REVIEW",
  draft: "DRAFT",
};
const _STATUS_COLORS = {
  ACTIVE: "#3FB950", "UNDER REVIEW": "#E0A526",
  ARCHIVED: "#B7B0A4", CLOSED: "#D92D20", RESTRICTED: "#3F3B36",
  DRAFT: "#7B7168", MONITORED: "#E0A526",
};
const _statusLabel = (raw) => {
  if (!raw) return "ACTIVE";
  const key = String(raw).toLowerCase();
  if (_STATUS_LABELS[key]) return _STATUS_LABELS[key];
  const upper = String(raw).toUpperCase();
  if (_STATUS_COLORS[upper]) return upper;
  return "ACTIVE";
};
const _statusDot = (status) => _STATUS_COLORS[status] || "#3FB950";

export const DossierHero = ({ topic }) => {
  const hero = _resolveImage(topic?.image_url || topic?.hero_image);
  if (!hero) return null;
  const status = _statusLabel(topic?.status || topic?.dossier_status);
  // Prefer backend-derived counts; fall back to raw array lengths for
  // topics fetched via legacy endpoints. Never multiply — placeholder
  // multipliers violate the "never invent data" editorial rule.
  const evidenceCount = topic?.evidence_count
    ?? ((topic?.sources?.length ?? 0) + (topic?.timeline?.length ?? 0));
  const sourceCount   = topic?.source_count   ?? topic?.sources?.length ?? 0;
  const readingTime   = topic?.reading_time_minutes;
  const connectionCount = topic?.connection_count;

  // Derive a short date-range from the timeline if the shape() layer
  // didn't already provide one.
  let range = topic?.date_range;
  if (!range && Array.isArray(topic?.timeline) && topic.timeline.length) {
    const years = [];
    for (const e of topic.timeline) {
      const y = e?.year;
      if (!y) continue;
      const m = String(y).match(/\b(1[6-9]\d{2}|20\d{2}|21\d{2})\b/);
      if (m) years.push(parseInt(m[1], 10));
    }
    if (years.length) {
      const lo = Math.min(...years), hi = Math.max(...years);
      range = lo === hi ? `${lo}` : `${lo} — ${hi}`;
    }
  }

  return (
    <section
      data-testid="dossier-hero"
      className="relative overflow-hidden mb-8 border"
      style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}
    >
      {/* Image */}
      <div className="aspect-[21/9] md:aspect-[21/8] w-full relative">
        <img
          src={hero}
          alt=""
          loading="eager"
          className="w-full h-full object-cover"
          style={{ filter: "saturate(0.8) contrast(1.05)" }}
        />
        {/* Cinematic gradient overlay so the meta strip stays legible */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(11,10,13,0.15) 0%, rgba(11,10,13,0.55) 55%, rgba(11,10,13,0.95) 100%)",
          }}
        />
        {/* Grid overlay for the intelligence-agency feel */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      {/* Metadata strip — under the image, above the CASE FILE panel */}
      <div className="px-5 md:px-8 py-3 md:py-4 flex flex-wrap items-center gap-3 md:gap-6 border-t"
           style={{ borderColor: "#2D2A26", background: "rgba(11,10,13,0.6)" }}>
        <div className="flex items-center gap-2">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: _statusDot(status) }}
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#E4DFD3]">
            {status}
          </span>
        </div>
        <div className="w-px h-4 bg-[#2D2A26]" />
        <div className="flex items-center gap-1.5">
          <FileText size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
            {evidenceCount} <span className="text-[#7B7168]">evidence</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Link2 size={12} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
            {sourceCount} <span className="text-[#7B7168]">sources</span>
          </span>
        </div>
        {range && (
          <div className="flex items-center gap-1.5">
            <Clock size={12} className="text-[#D92D20]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
              {range}
            </span>
          </div>
        )}
        {readingTime && (
          <div className="flex items-center gap-1.5" data-testid="dossier-hero-reading-time">
            <Clock size={12} className="text-[#7B7168]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
              {readingTime}m <span className="text-[#7B7168]">read</span>
            </span>
          </div>
        )}
        {connectionCount > 0 && (
          <div className="flex items-center gap-1.5" data-testid="dossier-hero-connections">
            <Link2 size={12} className="text-[#7B7168]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
              {connectionCount} <span className="text-[#7B7168]">connected</span>
            </span>
          </div>
        )}
      </div>
    </section>
  );
};

export default DossierHero;
